import { useState } from 'react';
import { Zap, Calendar, Flame, Gift } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useTranslation } from 'react-i18next';
import { performDailyCheckIn, getDailyCheckInReward, CREDIT_REWARDS } from '../../lib/credits';
import { getUtcToday, getUtcYesterday } from '../../lib/local-date';
import { checkMilestone } from '../../lib/daruma-milestones';

interface DailyCheckInProps {
  userId: string;
  currentStreak: number;
  lastCheckIn: string | null;
  onCheckInComplete: (result: { creditsEarned: number; streakDay: number; streakBonus?: number }) => void;
  onClose: () => void;
}

export function DailyCheckIn({ userId, currentStreak, lastCheckIn, onCheckInComplete, onClose }: DailyCheckInProps) {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{ creditsEarned: number; streakDay: number; streakBonus?: number } | null>(null);

  const today = getUtcToday();
  const alreadyCheckedIn = lastCheckIn === today;

  const yesterdayStr = getUtcYesterday();
  const willContinueStreak = lastCheckIn === yesterdayStr;
  const nextStreakDay = willContinueStreak ? currentStreak + 1 : 1;
  const previewReward = getDailyCheckInReward(nextStreakDay);

  const handleCheckIn = async () => {
    setChecking(true);
    const res = await performDailyCheckIn(userId);
    setChecking(false);

    if (res.success) {
      setResult({ creditsEarned: res.creditsEarned, streakDay: res.streakDay, streakBonus: res.streakBonus });
      onCheckInComplete({ creditsEarned: res.creditsEarned, streakDay: res.streakDay, streakBonus: res.streakBonus });
      checkMilestone('streak', res.streakDay);
    }
  };

  const windowStart = Math.max(1, nextStreakDay - 3);
  const streakDays = Array.from({ length: 7 }, (_, i) => {
    const day = windowStart + i;
    const reward = getDailyCheckInReward(day);
    const isToday = day === nextStreakDay && !result;
    const isPast = result ? day <= result.streakDay : day < nextStreakDay;
    const isCurrent = result ? day === result.streakDay : false;
    return { day, reward, isToday, isPast, isCurrent };
  });

  return (
    <Modal title={t('checkIn.title')} onClose={onClose}>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 text-center">
        {t('checkIn.subtitle')}
      </p>

      <div className="grid grid-cols-7 gap-1 mb-3">
        {streakDays.map(({ day, reward, isToday, isPast, isCurrent }) => (
          <div
            key={day}
            className={`relative flex flex-col items-center py-1 px-0.5 rounded-lg text-center transition-all ${
              isCurrent
                ? 'bg-gradient-to-b from-amber-500 to-orange-500 text-white shadow-md scale-110 ring-2 ring-amber-300'
                : isPast
                ? 'bg-amber-500/90 dark:bg-amber-600/80 text-white dark:text-white'
                : isToday
                ? 'bg-white dark:bg-amber-400 text-amber-700 dark:text-gray-900 border-2 border-amber-400 dark:border-amber-300 shadow-lg shadow-amber-400/30 scale-105 animate-pulse'
                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-300 dark:text-gray-500 border border-dashed border-gray-300 dark:border-gray-500'
            }`}
          >
            <span className="text-[10px] font-bold leading-tight">{t('checkIn.dayLabel', { day })}</span>
            <Zap size={10} className={isCurrent || isPast ? 'fill-current' : isToday ? 'fill-amber-500 dark:fill-gray-900' : ''} />
            <span className="text-[9px] font-bold leading-tight">{reward}</span>
          </div>
        ))}
      </div>

      {currentStreak > 0 && !result && (
        <div className="flex items-center justify-center gap-2 mb-3 py-1.5 px-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50">
          <Flame size={14} className="text-orange-500" />
          <span className="text-xs font-semibold text-orange-700 dark:text-orange-300">
            {t('checkIn.streak', { count: currentStreak })}
          </span>
        </div>
      )}

      {result ? (
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
            <Zap size={18} className="fill-current" />
            <span className="text-lg font-bold">+{result.creditsEarned}</span>
          </div>
          {result.streakBonus && (
            <div className="flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400">
              <Gift size={14} />
              <span className="text-xs font-bold">
                {t('checkIn.streakBonus', { bonus: result.streakBonus })}
              </span>
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('checkIn.dayStreak', { day: result.streakDay })}
            {result.streakDay === 7 && ` — ${t('checkIn.milestone7')}`}
            {result.streakDay === 30 && ` — ${t('checkIn.milestone30')}`}
          </p>
          <Button onClick={onClose} variant="outline" size="sm">
            {t('common.close')}
          </Button>
        </div>
      ) : alreadyCheckedIn ? (
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/50 text-success-700 dark:text-success-300">
            <Calendar size={14} />
            <span className="text-sm font-bold">{t('checkIn.alreadyCheckedIn')}</span>
          </div>
          <p className="text-xs text-gray-400">{t('checkIn.comeBackTomorrow')}</p>
        </div>
      ) : (
        <div className="text-center">
          <Button
            onClick={handleCheckIn}
            disabled={checking}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 shadow-lg"
          >
            {checking ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('checkIn.checkingIn')}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Zap size={16} className="fill-current" />
                {t('checkIn.claimCredits', { count: previewReward })}
              </span>
            )}
          </Button>
          {nextStreakDay === 7 && (
            <p className="text-xs text-amber-500 font-semibold mt-2">
              {t('checkIn.bonus7', { bonus: CREDIT_REWARDS.streak_bonus_7 })}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
